<template>
  <div class="p-4">
    <!-- Изображения: карусель 16:9, контейнер фиксирован, картинка вписывается -->
    <div class="mb-4 w-full">
      <div class="aspect-video relative border rounded-md overflow-hidden bg-gray-100 w-full" style="aspect-ratio: 16/9;">
        <template v-if="currentImages.length > 0">
          <img
            :src="currentImageSrc"
            alt="Изображение шага"
            class="absolute inset-0 w-full h-full object-contain"
          />
          <button
            v-if="currentImages.length > 1"
            @click.stop="prevSlide"
            class="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            v-if="currentImages.length > 1"
            @click.stop="nextSlide"
            class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <span
            v-if="currentImages.length > 0"
            class="absolute bottom-2 left-2 px-2 py-1 rounded bg-white/90 text-sm text-gray-700 font-medium"
          >
            {{ currentSlide + 1 }}/{{ currentImages.length }}
          </span>
        </template>
        <div v-else class="flex items-center justify-center h-full text-gray-500 text-center p-2">
          <p>Изображение не загружено</p>
        </div>
        <button
          @click.stop="$emit('upload-image', step)"
          class="absolute bottom-2 right-2 w-10 h-10 flex items-center justify-center bg-white bg-opacity-80 rounded-full shadow hover:bg-opacity-100 text-lg"
          title="Загрузить изображение"
        >
          📷
        </button>
      </div>
    </div>

    <!-- Контент под изображением -->
    <div>
      <div class="mb-4">
        <textarea
          ref="descriptionField"
          :value="step.description"
          @input="onDescriptionInput"
          rows="1"
          placeholder="Добавьте описание шага..."
          class="w-full resize-none whitespace-pre-line bg-transparent border border-transparent rounded px-2 py-1 text-gray-800 hover:border-gray-300 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        />
      </div>

      <div v-if="step.products?.length > 0" class="mt-4">
        <h4 class="font-semibold mb-2">{{ step.type === 'software' ? 'Используемое ПО:' : 'Используемые продукты:' }}</h4>
        <table class="w-full border-collapse border border-gray-300">
          <tbody>
            <tr v-for="(product, index) in step.products"
                :key="`${product.id}-${index}-${Date.now()}`"
                class="hover:bg-gray-50 border-b border-gray-300">
              <td class="p-1 w-8 text-center">
                <span class="font-medium">{{ index + 1 }}.</span>
              </td>
              <td class="p-1">
                <span :class="{ 'line-through text-gray-500': product.deleted_at }" class="font-medium">{{ getProductDisplayName(product) }}</span>
                <span v-if="product.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
              </td>
              <td class="p-1 w-24">
                <span class="inline-flex items-center w-full">
                  <svg v-if="product.is_single" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" class="w-8 h-8 flex-shrink-0 text-gray-600 fill-current" title="Серийная продукция">
                    <path d="m15.713 46.98v105.4h168.66v-105.4h-168.66zm5 5h158.66v95.405h-158.66v-95.405zm96.025 21.139c-2.069 0.013-9.322 0.133-9.343 0.154-0.04 0.04-0.019 51.198 0.021 51.957l0.039 0.76 0.477 0.028c0.261 0.014 2.562 0.022 5.113 0.019 4.064-5e-3 4.649-0.018 4.732-0.101s0.098-0.617 0.108-4.188c6e-3 -2.251 0.03-5.99 0.054-8.31 0.025-2.321 0.054-6.483 0.065-9.25 0.019-4.71 0.075-7.538 0.15-7.614 0.085-0.085 0.325 0.274 0.701 1.047 0.227 0.466 0.517 1.031 0.645 1.254s0.585 1.095 1.016 1.937c0.43 0.843 1.091 2.122 1.468 2.844 0.612 1.17 0.965 1.837 1.969 3.719 0.156 0.292 0.853 1.614 1.549 2.937 1.804 3.431 3.05 5.802 3.715 7.063 0.317 0.602 0.825 1.572 1.127 2.156 1.581 3.061 3.303 6.169 3.535 6.379 0.115 0.104 0.268 0.119 1.422 0.141 0.711 0.013 1.307 0.038 1.324 0.056 0.061 0.066 7.524-0.043 7.592-0.111 0.044-0.044 0.077-2.677 0.103-8.141 0.054-11.105 0.071-44.465 0.024-44.543-0.026-0.041-1.828-0.062-5.244-0.062h-5.205l-0.041 3.479c-0.05 4.172-0.042 18.188 0.013 22.302 0.022 1.616 0.025 3.177 8e-3 3.469l-0.031 0.531-0.164-0.219c-0.176-0.235-0.805-1.293-0.805-1.353 0-0.02-0.138-0.279-0.305-0.576s-0.464-0.849-0.662-1.227-0.437-0.814-0.531-0.968c-0.094-0.155-0.714-1.336-1.377-2.626-0.663-1.289-1.249-2.414-1.303-2.5-0.053-0.085-0.593-1.112-1.201-2.281-0.607-1.168-1.378-2.645-1.713-3.281-0.334-0.636-0.785-1.494-1.002-1.906-0.216-0.413-0.954-1.819-1.64-3.125-1.586-3.018-3.08-5.909-3.86-7.469-0.7-1.402-1.104-2.12-1.277-2.277-0.107-0.097-0.255-0.11-1.266-0.104zm-39.281 0.078c-2.941-0.054-6.329 0.356-8.992 1.11-3.391 0.959-5.32 1.885-7.41 3.552-1.705 1.36-2.928 2.801-3.885 4.579-1.003 1.862-1.581 4.671-1.363 6.624 0.139 1.247 0.305 1.945 0.724 3.034 1.031 2.676 2.875 4.984 5.25 6.572 2.014 1.346 3.172 1.827 7.203 2.984 3.996 1.147 7.729 1.988 11.797 2.657 2.948 0.484 3.85 0.658 4.418 0.851 1.187 0.404 2.393 1.188 3.071 1.996 0.457 0.547 0.637 0.851 0.841 1.424 0.145 0.405 0.162 0.546 0.164 1.358 3e-3 0.678-0.024 1.008-0.107 1.312-0.684 2.516-2.34 4.144-5.186 5.094-2.106 0.703-5.52 0.919-8.044 0.51-4.674-0.759-7.41-2.49-9.016-5.7-0.47-0.939-0.891-2.012-1.051-2.685-0.262-1.102-0.321-1.252-0.496-1.24-0.086 5e-3 -0.466 0.04-0.844 0.076s-1.447 0.135-2.375 0.222c-3.464 0.325-6.357 0.649-6.429 0.721-0.061 0.06 0.061 0.861 0.341 2.248 0.794 3.933 2.41 7.268 4.78 9.861 2.082 2.279 5.335 3.904 9.464 4.731 2.256 0.451 5.291 0.813 7.563 0.902 2.475 0.097 4.562-0.098 7.687-0.719 4.652-0.923 7.161-2.12 9.661-4.613 0.953-0.951 1.499-1.667 2.177-2.855 0.439-0.768 1.113-2.243 1.338-2.928 0.506-1.535 0.815-3.674 0.737-5.082-0.067-1.183-0.264-2.755-0.465-3.699-0.211-0.99-0.284-1.221-0.68-2.188-0.705-1.719-1.426-2.821-2.639-4.035-1.381-1.382-2.889-2.343-4.785-3.049-0.983-0.366-1.636-0.529-3.718-0.922-4.21-0.794-7.362-1.508-10.157-2.302-0.653-0.186-1.708-0.484-2.343-0.662-5.306-1.487-6.938-2.664-6.938-5.008 0-1.183 0.572-2.365 1.68-3.471 0.494-0.494 1.336-1.119 1.877-1.393 1.36-0.689 4.082-1.254 6.037-1.252 1.625 2e-3 4.315 0.454 5.797 0.971 1.018 0.356 2.075 1.066 2.621 1.762 0.743 0.947 1.495 2.349 2.021 3.767 0.392 1.057 0.394 1.063 0.608 1.063 0.101 0 0.652-0.027 1.224-0.061 0.573-0.033 1.688-0.09 2.479-0.126 2.542-0.118 4.927-0.248 5.301-0.29 0.419-0.046 0.55-0.144 0.511-0.384-0.262-1.607-0.748-3.495-1.187-4.61-0.327-0.829-1.043-2.21-1.51-2.914-1.578-2.378-3.362-3.953-5.865-5.178-2.965-1.449-6.38-2.271-10.656-2.562-0.399-0.027-0.811-0.045-1.231-0.053z"/>
                  </svg>
                  <span class="ml-auto text-gray-600">x{{ product.count }}</span>
                </span>
              </td>
              <td class="p-1 w-20 text-center">
                <div class="flex items-center justify-center space-x-1">
                  <button
                    @click.stop="$emit('edit-product', { stepId: step.id, productIndex: index, product })"
                    class="text-blue-600 hover:text-blue-800 p-1"
                    title="Редактировать"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    @click.stop="confirmProductRemoval(index)"
                    class="text-red-600 hover:text-red-800 p-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="step.type === 'print' && step.documents?.length > 0" class="mt-4">
        <h4 class="font-semibold mb-2">Документы:</h4>
        <div class="border rounded divide-y">
          <div v-for="(doc, index) in step.documents"
               class="p-2 hover:bg-gray-50 flex justify-between items-center"
               :key="`${doc.id}-${index}-${Date.now()}`"
          >
            <div>
              <span class="font-medium">{{ doc.name }}</span>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click.stop="$emit('remove-document', { step, index })"
                class="p-1 text-red-600 hover:text-red-800"
                title="Удалить документ"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Прикреплённые для печати файлы -->
      <div v-if="printFiles.length > 0" class="mt-4">
        <h4 class="font-semibold mb-2">Прикреплённые файлы:</h4>
        <div class="border rounded divide-y">
          <div v-for="(file, index) in printFiles"
               :key="`print-${file.file_id || file.file?.id}-${index}`"
               class="p-2 hover:bg-gray-50 flex justify-between items-center">
            <a
              :href="`/api/files/${file.file_id || file.file?.id}/preview`"
              target="_blank"
              class="font-medium text-blue-600 hover:underline truncate"
            >
              {{ file.file?.original_name || file.file?.name || 'Документ' }}
            </a>
            <button
              @click.stop="removePrintFile(index)"
              class="p-1 text-red-600 hover:text-red-800"
              title="Удалить файл"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <input
        ref="printFileInput"
        type="file"
        accept=".pdf,application/pdf"
        class="hidden"
        @change="handlePrintFileSelect"
      />

      <div class="flex space-x-2 mt-4">
        <button
          @click="$emit('add-product', step)"
          class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center"
        >
          <span class="mr-1">+</span> {{ step.type === 'software' ? 'Добавить ПО' : 'Добавить продукт' }}
        </button>
        <button
          v-if="showImportButton"
          @click="$emit('import-excel')"
          class="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
        >
          Импорт из Excel
        </button>
      </div>

      <div class="flex space-x-2 mt-2">
        <button
          v-if="step.type === 'print'"
          @click="$emit('add-document', step)"
          class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center"
        >
          <span class="mr-1">+</span> Добавить документ
        </button>
        <button
          @click="triggerPrintFileUpload"
          :disabled="isUploadingPrintFile"
          class="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span class="mr-1">📎</span> {{ isUploadingPrintFile ? 'Загрузка...' : 'Прикрепить файл' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    step: { type: Object, required: true },
    productOptions: { type: Array, default: () => [] },
    availableDocuments: { type: Array, default: () => [] },
    showImportButton: { type: Boolean, default: false }
  },
  data() {
    return { currentSlide: 0, isUploadingPrintFile: false };
  },
  computed: {
    printFiles() {
      return Array.isArray(this.step.print_files) ? this.step.print_files : [];
    },
    currentImages() {
      if (Array.isArray(this.step.images) && this.step.images.length > 0) {
        return this.step.images;
      }
      return [];
    },
    currentImageSrc() {
      const img = this.currentImages[this.currentSlide];
      return img?.url || '';
    }
  },
  mounted() {
    this.autoResizeDescription();
  },
  watch: {
    'step.id'() {
      this.currentSlide = 0;
      this.$nextTick(this.autoResizeDescription);
    },
    'step.description'() {
      this.$nextTick(this.autoResizeDescription);
    },
    currentImages() {
      if (this.currentSlide >= this.currentImages.length) {
        this.currentSlide = Math.max(0, this.currentImages.length - 1);
      }
    }
  },
  methods: {
    onDescriptionInput(event) {
      this.step.description = event.target.value;
      this.autoResizeDescription();
      this.$emit('change');
    },
    autoResizeDescription() {
      const el = this.$refs.descriptionField;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    },
    triggerPrintFileUpload() {
      if (this.isUploadingPrintFile) return;
      this.$refs.printFileInput?.click();
    },
    async handlePrintFileSelect(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (file.type !== 'application/pdf') {
        alert('Допустимы только PDF-файлы');
        return;
      }
      this.isUploadingPrintFile = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post('/api/files', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (!data.file?.id) throw new Error('Файл не загружен');
        if (!Array.isArray(this.step.print_files)) {
          this.step.print_files = [];
        }
        this.step.print_files.push({ file_id: data.file.id, file: data.file });
        this.$emit('change');
      } catch (error) {
        console.error('Ошибка загрузки файла:', error);
        alert(error.response?.data?.message || 'Не удалось загрузить файл');
      } finally {
        this.isUploadingPrintFile = false;
      }
    },
    removePrintFile(index) {
      if (!Array.isArray(this.step.print_files)) return;
      this.step.print_files.splice(index, 1);
      this.$emit('change');
    },
    prevSlide() {
      this.currentSlide = this.currentSlide <= 0 ? this.currentImages.length - 1 : this.currentSlide - 1;
    },
    nextSlide() {
      this.currentSlide = this.currentSlide >= this.currentImages.length - 1 ? 0 : this.currentSlide + 1;
    },
    confirmProductRemoval(index) {
      console.log('Initiating product removal', {
        step: this.step,
        index
      });
      this.$emit('remove-product', {
        step: this.step,
        index
      });
    },
    getProductDisplayName(product) {
      const hasSku = product.sku && product.sku.trim() !== '';
      const hasName = product.name && product.name.trim() !== '';
      
      if (hasSku && hasName) {
        return `${product.sku} - ${product.name}`;
      } else if (hasSku) {
        return product.sku;
      } else if (hasName) {
        return product.name;
      } else {
        return 'Без названия';
      }
    }
  }

};
</script>
